import React, { useState, useRef, useEffect } from "react";
import { Globe, CaretDown } from "@phosphor-icons/react";
import { useI18n } from "../contexts/I18nContext";

/**
 * Language picker — drives the "lang" used when generating EU24
 * status-report exports. Selection persists to localStorage.
 *
 * Compact button + flyout list. Dark-on-dark to match the Layout chrome.
 */
export default function LanguagePicker() {
  const { lang, setLang, languages } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Click-outside close
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const current = languages.find((l) => l.code === lang) || languages[0];

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-picker-btn"
        title="Status report language"
        className="flex items-center gap-2 px-2.5 py-1.5 border border-zinc-800 hover:border-amber/60 hover:bg-[#141414] text-xs text-zinc-300 transition-colors"
      >
        <Globe size={14} weight="regular" className="text-amber" />
        <span className="font-mono uppercase tracking-wider text-[10px]" data-testid="language-picker-current">
          {current?.code || "en"}
        </span>
        <CaretDown size={10} weight="bold" className="text-zinc-500" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 w-64 max-h-[60vh] overflow-y-auto z-50 bg-[#0F0F0F] border border-zinc-800 shadow-xl"
          data-testid="language-picker-menu"
          role="listbox"
        >
          <div className="px-3 py-2 text-[9px] uppercase tracking-[0.25em] text-zinc-500 border-b border-zinc-800 font-mono">
            Report language · {languages.length}
          </div>
          {languages.map((l) => {
            const selected = l.code === lang;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => { setLang(l.code); setOpen(false); }}
                data-testid={`language-option-${l.code}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left transition-colors ${
                  selected
                    ? "bg-[#1F1F1F] text-amber border-l-2 border-amber"
                    : "text-zinc-300 hover:bg-[#141414] border-l-2 border-transparent"
                }`}
              >
                <span className="text-base leading-none">{l.flag}</span>
                <span className="font-mono uppercase tracking-wider text-[10px] w-7 shrink-0">{l.code}</span>
                <span className="flex-1 truncate">{l.name_native}</span>
                <span className="text-[10px] text-zinc-500 truncate max-w-[80px] hidden sm:inline">{l.name_en}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
